import type { BiometricDeletionRepository } from '@/modules/admin/domain/repositories/BiometricDeletionRepository';
import type { LocalEmployeeRepository } from '@/modules/employees/infrastructure/repositories/LocalEmployeeRepository';
import type { LocalFaceEmbeddingRepository } from '@/modules/attendance/infrastructure/repositories/LocalFaceEmbeddingRepository';
import { enqueueSyncOperation } from '@/shared/sync/syncQueue';

export interface DeleteFacialEmbeddingInput {
  idEntity: number;
  idPerson: number;
}

/**
 * Use case: delete the facial v2 embedding for an employee.
 *
 * OFFLINE-FIRST UPDATE (2026-07-23):
 * New sequence (works offline):
 *  1. Patch local Dexie employee cache immediately (nulls person_embed_v2 + filters legacy).
 *  2. Delete from `db.faceEmbeddings` immediately (offline matcher stops recognizing).
 *  3. Enqueue sync operation for backend deletion (processes when online).
 *
 * OLD SEQUENCE (online-only, commented below for rollback):
 *  1. Call remote adapter first → 404 is idempotent.
 *  2. Then patch local Dexie cache.
 *  3. Then delete from offline matcher cache.
 *
 * Rationale for order change:
 *  - Local changes must happen FIRST (offline-first pattern).
 *  - Sync queue handles backend deletion when connection is available.
 *  - UI updates immediately (doesn't wait for backend).
 *
 * Change: nueva-seccion-de-borrado-perfiles-en-administracion-empleados
 * Updated: admin-employee-detail-followups — adds step 3 (offline matcher cache cleanup).
 * Updated: enable-offline-deletion (2026-07-23) — offline-first sequence.
 */
export class DeleteFacialEmbeddingUseCase {
  // OLD: biometricDeletionRepo was used for direct backend call (online-only)
  // Now unused - kept for rollback compatibility
  // @ts-expect-error - Intentionally unused (offline-first migration)
  private readonly biometricDeletionRepo: BiometricDeletionRepository;
  
  constructor(
    biometricDeletionRepo: BiometricDeletionRepository,
    private readonly localEmployeeRepo: Pick<LocalEmployeeRepository, 'removeBiometricData'>,
    private readonly localFaceEmbeddingRepo: Pick<LocalFaceEmbeddingRepository, 'deleteByIdPerson'>,
  ) {
    this.biometricDeletionRepo = biometricDeletionRepo;
  }

  async execute(input: DeleteFacialEmbeddingInput): Promise<void> {
    const { idPerson } = input;
    
    try {
      // OFFLINE-FIRST: Update local state immediately
      // Step 1: Clean employee cache (nulls person_embed_v2)
      await this.localEmployeeRepo.removeBiometricData(idPerson, { kind: 'facial' });
      
      // Step 2: Clean offline matcher cache (stops facial recognition)
      await this.localFaceEmbeddingRepo.deleteByIdPerson(idPerson);
      
      // Step 3: Enqueue sync operation for backend deletion
      // Uses stable ID so multiple deletes of same person don't create duplicate queue items
      await enqueueSyncOperation({
        id: `delete-facial-${idPerson}`,  // Stable ID for idempotency
        module: 'face_embedding_v2',
        operation: 'delete',
        endpoint: `/v1/clock/app/person/${idPerson}/embed`,  // NOTE: /embed NOT /enroll/v2/embed
        payload: {},  // DELETE doesn't need body
      });
    } catch (err) {
      // If any step fails, propagate the error (caller can retry)
      // Step 3 (enqueueSyncOperation) might fail if IndexedDB is unavailable
      throw err;
    }
  }
}

// ════════════════════════════════════════════════════════════════════════════
// OLD IMPLEMENTATION (online-only) — COMMENTED FOR ROLLBACK
// ════════════════════════════════════════════════════════════════════════════
// async execute(input: DeleteFacialEmbeddingInput): Promise<void> {
//   const { idPerson } = input;
//   // OLD: Call backend first (required online)
//   await this.biometricDeletionRepo.deleteFacialEmbedding(idPerson);
//   // Then update local cache
//   await this.localEmployeeRepo.removeBiometricData(idPerson, { kind: 'facial' });
//   await this.localFaceEmbeddingRepo.deleteByIdPerson(idPerson);
// }
// ════════════════════════════════════════════════════════════════════════════
